import { useState, useEffect, useCallback } from 'react';
import { SheetData } from '../services/lunchOrdersApiService';

export type HighlightColor = 'green' | 'red' | 'yellow' | null;

const STORAGE_KEY = 'lci-lunch-highlights';
const COLOR_CYCLE: HighlightColor[] = [null, 'green', 'red', 'yellow'];

function makeKey(sheetName: string, row: number, col: number) {
  return `${sheetName}:${row}:${col}`;
}

function loadHighlights(): Record<string, HighlightColor> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveHighlights(highlights: Record<string, HighlightColor>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(highlights));
}

function cleanStaleHighlights(
  highlights: Record<string, HighlightColor>,
  sheets: SheetData[],
): Record<string, HighlightColor> {
  const tabNames = new Set(sheets.map((s) => s.name));
  const cleaned: Record<string, HighlightColor> = {};
  for (const key of Object.keys(highlights)) {
    const sheetName = key.substring(0, key.indexOf(':'));
    if (tabNames.has(sheetName)) {
      cleaned[key] = highlights[key];
    }
  }
  return cleaned;
}

export function useCellHighlights(sheets: SheetData[]) {
  const [highlights, setHighlights] = useState<Record<string, HighlightColor>>(loadHighlights);

  // Clean stale entries when sheets change
  useEffect(() => {
    if (sheets.length === 0) return;
    setHighlights((prev) => {
      const cleaned = cleanStaleHighlights(prev, sheets);
      saveHighlights(cleaned);
      return cleaned;
    });
  }, [sheets]);

  const getHighlight = useCallback(
    (sheetName: string, row: number, col: number): HighlightColor => {
      return highlights[makeKey(sheetName, row, col)] ?? null;
    },
    [highlights],
  );

  const cycleHighlight = useCallback(
    (sheetName: string, row: number, col: number) => {
      setHighlights((prev) => {
        const key = makeKey(sheetName, row, col);
        const current = prev[key] ?? null;
        const nextIndex = (COLOR_CYCLE.indexOf(current) + 1) % COLOR_CYCLE.length;
        const next = COLOR_CYCLE[nextIndex];
        const updated = { ...prev };
        if (next === null) {
          delete updated[key];
        } else {
          updated[key] = next;
        }
        saveHighlights(updated);
        return updated;
      });
    },
    [],
  );

  return { getHighlight, cycleHighlight };
}
